import InputLockable from "@components/shared/forms/input-lockable";
import { useAdsChatIsStreaming } from "@components/ads/hooks";
import { Field, FieldError } from "@components/ui/field";
import { Ads } from "@shared";
import type { Control, FieldArrayWithId } from "react-hook-form";
import { Controller } from "react-hook-form";
import z from "zod";

export const adCampaignSchema = z.object({
  adGroupName: z.string().min(1, "Ad group name is required"),
  headlines: Ads.HeadlinesFormSchema,
  descriptions: Ads.DescriptionsFormSchema,
  features: z.array(Ads.AssetSchema),
  callouts: Ads.CalloutsFormSchema,
});

type AdCampaignFormData = z.infer<typeof adCampaignSchema>;

interface AdCampaignFieldListProps {
  fieldName: "headlines" | "descriptions" | "features" | "callouts" | "details" | "keywords" | "negative_keywords";
  fields: FieldArrayWithId<
    AdCampaignFormData,
    "headlines.headlines" | "descriptions.descriptions" | "features" | "callouts.callouts",
    "id"
  >[];
  control: Control<AdCampaignFormData>;
  onLockToggle?: (
    fieldName: "headlines" | "descriptions" | "features" | "callouts" | "details" | "keywords" | "negative_keywords",
    index: number
  ) => void;
  onDelete?: (index: number) => void;
  placeholder: string;
  maxLength: number;
  resolveIndex?: (id: string) => number;
  onInputChange?: (index: number, input: string) => void;
  isPlaceholder?: boolean;
}

export default function AdCampaignFieldList({
  fieldName,
  fields,
  control,
  onLockToggle,
  onDelete,
  placeholder,
  maxLength,
  resolveIndex,
  onInputChange,
  isPlaceholder = false,
}: AdCampaignFieldListProps) {
  const isStreaming = useAdsChatIsStreaming();
  const handleLockToggle = (index: number) => {
    if (!onLockToggle) return;
    onLockToggle(fieldName, index);
  };

  const handleDelete = (index: number) => {
    if (!onDelete) return;
    onDelete(index);
  };

  return (
    <>
      {fields.map((rawField, index: number) => {
        const field = rawField as unknown as Ads.Asset & { id: string };
        const originalIndex = resolveIndex ? resolveIndex(field.id) : index;
        return (
          <Controller
            key={index}
            name={`${fieldName}.${originalIndex}.text` as any}
            control={control}
            render={({ field: controllerField, fieldState }) => (
              <Field className="gap-1">
                <InputLockable
                  placeholder={placeholder}
                  value={controllerField.value as string}
                  onChange={(e) => {
                    controllerField.onChange(e);
                    if (onInputChange) {
                      onInputChange(originalIndex, e.target.value);
                    }
                  }}
                  onBlur={controllerField.onBlur}
                  name={controllerField.name as string}
                  isLocked={field.locked}
                  isShimmering={(isStreaming && !field.locked) || isPlaceholder}
                  onLockToggle={onLockToggle ? () => handleLockToggle(originalIndex) : undefined}
                  onDelete={onDelete ? () => handleDelete(originalIndex) : undefined}
                />
                <div className="flex">
                  {fieldState.error && <FieldError errors={[fieldState.error]} />}
                  <div className="text-right text-xs text-[#8b8b8b] ml-auto">
                    {(controllerField.value as string)?.length ?? 0}/{maxLength}
                  </div>
                </div>
              </Field>
            )}
          />
        );
      })}
    </>
  );
}
